import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { CartContext } from "components/cart";
import Bundles from "./Bundles";
import useCampaign from "hooks/useCampaign";
import classNames from "lib/classNames";
import Announcement from "./announcement";
import GiftWithPurchaseWidget from "./GiftWithPurchaseWidget";
import { isOCUProduct, getAllOCUProducts } from "./CheckoutButton";

const CartItemsList = (props) => {
  const {
    campaigns,
    shopify_cart_items,
    cart_settings,
    cart_settings: {
      announcement = {}
    },
    productAddedToCart,
    discountCode
  } = useContext(CartContext);
  const [one_check_up_sell_product_id, ocu_campaign] = useCampaign(
    "one_check_up_sell", null, true
  )(campaigns, shopify_cart_items);
  const ocu_products = getAllOCUProducts(ocu_campaign)
  const filteredItems = shopify_cart_items.filter((item) => {
    const ocu_product = ocu_products.find(({ product }) => item.product_id.toString() === product?.id)
    return ocu_product ? ocu_product.include_in_atu : item?.product_type?.toLowerCase() !== "free_gift"
  });
  const [add_to_unlock_product_id, add_to_unlock] = useCampaign("add_to_unlock")(campaigns, shopify_cart_items);
  const giftGoals = add_to_unlock?.goals?.filter(({ reward_type, gift }) => reward_type === "gift" && gift?.product?.title &&
    gift?.widget?.active)
  const CAN_RENDER_GIFT_WIDGET = !discountCode &&
    add_to_unlock?.isActive &&
    giftGoals.length &&
    productAddedToCart({ id: add_to_unlock_product_id });
  return (
    <div
      className={classNames("flex flex-col px-5 pt-4", {
        "flex-1": cart_settings.product.grow
      })}
    >
      {!!shopify_cart_items.length && announcement?.isActive && announcement.position === "above_products" && (
        <Announcement announcement={announcement} />
      )}
      {!!shopify_cart_items.length &&
        [...shopify_cart_items]
          .reverse()
          .map((cartItem, key) => (
            <CartItem
              cartItem={cartItem}
              key={key}
              giftGoals={giftGoals}
              line={shopify_cart_items.length - key}
            />
          ))}
      {!!CAN_RENDER_GIFT_WIDGET && giftGoals.map((goal, key) => <GiftWithPurchaseWidget key={key} giftGoal={goal} filteredItems={filteredItems} multi_goal_type={add_to_unlock.multi_goal_type} />)}
      {!!shopify_cart_items.length && announcement?.isActive && announcement.position === "bellow_products" && (
        <Announcement announcement={announcement} />
      )}
    </div>
  );
};

CartItemsList.propTypes = {};

const CartItem = ({ cartItem, line, giftGoals }) => {
  const {
    id,
    image,
    product_title,
    quantity,
    price,
    product_id,
    variant_id,
    url,
    itemSoldOut,
    variant_title,
    selling_plan_allocation,
    properties
  } = cartItem;
  const [error, setError] = useState(null);
  const [soldOut, setSoldOut] = useState(!!itemSoldOut);
  const [maxQuantity, setMaxQuantity] = useState(Infinity);
  const {
    campaigns,
    update_cart_item,
    Utils,
    isPreview,
    cart_settings: {
      theme: { background },
      product,
      money_format,
      rtl
    },
    shopify_cart_items,
    productAddedToCart,
    discountCode,
    removeFBTUpsell,
    compareAtMap
  } = useContext(CartContext);
  const [one_check_up_sell_product_id, ocu_campaign] = useCampaign(
    "one_check_up_sell", null, true
  )(campaigns, shopify_cart_items);
  const [_, bundles] = useCampaign("bundles", product_id)(
    campaigns,
    shopify_cart_items
  );
  const IS_EXAMPLE = isPreview && product_id < 10;
  const IS_ONE_CHECK_PRODUCT =
    shopify_cart_items.length > 1 &&
    productAddedToCart(one_check_up_sell_product_id) &&
    isOCUProduct(ocu_campaign, product_id, IS_EXAMPLE)
  const IS_GIFT_PRODUCT = !!giftGoals?.find(({ gift }) => gift?.product?.variant_id === variant_id?.toString())
  const updateItem = (step) => {
    const newQuantity = quantity + step;
    update_cart_item(
      { line, variant_id: id, quantity: newQuantity },
      (item, items, error) => {
        if (error) {
          update_cart_item(
            { line, variant_id: id, quantity: 0, sold_out: true },
            () => {
              setSoldOut(true);
              setMaxQuantity(0);
            }
          );
        }
        // else if (item && item.quantity !== newQuantity) {
        //   setMaxQuantity(item.quantity);
        // }
      }
    );
    if (newQuantity === 0) removeFBTUpsell(id);
  };
  const formatText = (text) => {
    const { price, original } = getPrice();
    return text
      .replace("{{product_title}}", product_title)
      .replace("{{product_price}}", Utils.formatMoney(price, money_format))
      .replace(
        "{{original_price}}",
        original
          ? `<del class='mu-opacity-75 ${rtl ? "mu-ml-1" : "mu-mr-1"
          }' style='font-size:0.8em'>${Utils.formatMoney(
            original,
            money_format
          )}</del> `
          : ""
      );
  };
  const hasCompareAt = (variant_id, quantity, price = null) => {
    const finalPrice = compareAtMap && compareAtMap[variant_id] ? compareAtMap[variant_id] : price
    // const finalPrice = compareAtMap && compareAtMap[variant_id] ? compareAtMap[variant_id] * parseFloat(window.Shopify.currency.rate) : price
    return finalPrice * quantity
  }

  const getPrice = () => {
    if (!bundles) return { price: price * quantity, original: hasCompareAt(id, quantity) };
    const selectedBundle =
      bundles.list.find((bundle) => bundle.quantity === quantity) ||
      Utils.sortByQuantity(bundles.list).find(
        (bundle) => bundle.quantity < quantity
      );
    if (!selectedBundle) return { price: price * quantity, original: hasCompareAt(id, quantity) };
    const discountedPrice = Utils.getBundleDiscount(
      selectedBundle,
      quantity,
      price,
      money_format
    );
    return { price: discountedPrice.plain, original: hasCompareAt(id, quantity, price) };
  };
  return (IS_ONE_CHECK_PRODUCT || IS_GIFT_PRODUCT) ? null : (
    <div className={classNames("mb-6  cart-item")}>
      <div className={classNames("flex")}>
        <div
          className={classNames("bg-cover bg-center block", {
            "ml-2": rtl,
            "mr-2": !rtl
          })}
          style={{
            backgroundImage: `url(${image})`,
            width: `${product.image_size}px`,
            height: `${product.image_size}px`
          }}
        />
        <div className={classNames("flex-1 flex-col")}>
          <div
            className={classNames("mb-2 overflow-hidden font-bold line-clamp")}
            style={{
              fontSize: Utils.em(product.title.font_size),
              color: Utils.formatColor(product.title.color),
              fontWeight: product.title.font_weight
            }}
          >
            {isPreview ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: formatText(product.title.text)
                }}
              />
            ) : (
              <a
                href={`${window.origin}${url}`}
                dangerouslySetInnerHTML={{
                  __html: formatText(product.title.text)
                }}
              />
            )}
          </div>
          {variant_title && <p>{variant_title.replace("/", "•")}</p>}
          {/* {properties &&
            Object.keys(properties).length === 1 &&
            Object.keys(properties)[0].toLowerCase() !== "image" && (
              <p className={classNames("text-xs")} style={{ opacity: 0.8 }}>
                {Object.keys(properties)[0]}:{" "}
                {properties[Object.keys(properties)[0]]}
              </p>
            )} */}
          {selling_plan_allocation?.selling_plan && (
            <p className={classNames("text-xs")}>
              {selling_plan_allocation?.selling_plan?.name}
            </p>
          )}
          {window?.mu_form_files &&
            window?.mu_form_files[id] &&
            window.mu_form_files[id].map(
              (name) =>
                properties[name] && (
                  <div className={classNames("my-2")} key={name}>
                    <p className={classNames("mb-1 capitalize")}>{name}</p>
                    <div
                      className={classNames(
                        "w-8 h-8 rounded-sm bg-center bg-cover"
                      )}
                      style={{ backgroundImage: `url(${properties[name]})` }}
                    />
                  </div>
                )
            )}
          <div className={classNames("flex items-center")}>
            <b
              className={classNames("flex-1")}
              style={{
                fontSize: Utils.em(product.price.font_size),
                fontWeight: product.price.font_weight,
                color: Utils.formatColor(product.price.color)
              }}
              dangerouslySetInnerHTML={{
                __html: formatText(product.price.text)
              }}
            />

            {!soldOut && (
              <div className={classNames("flex items-stretch")}>
                <button
                  style={{
                    border: "1px solid",
                    color: Utils.formatColor(product.title.color),
                    borderColor: Utils.lighterColor(0.8, product.title.color)
                  }}
                  className={classNames(
                    "border flex-center p-1 xs:p-2 bg-transparent",
                    {
                      "rounded-r rounded-l-none": rtl,
                      "rounded-l rounded-r-none": !rtl
                    }
                  )}
                  onClick={() => updateItem(-1)}
                >
                  {quantity === 1 && (
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      data-icon="trash"
                      style={{ height: 12 }}
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path
                        fill="currentColor"
                        d="M0 84V56c0-13.3 10.7-24 24-24h112l9.4-18.7c4-8.2 12.3-13.3 21.4-13.3h114.3c9.1 0 17.4 5.1 21.5 13.3L312 32h112c13.3 0 24 10.7 24 24v28c0 6.6-5.4 12-12 12H12C5.4 96 0 90.6 0 84zm415.2 56.7L394.8 467c-1.6 25.3-22.6 45-47.9 45H101.1c-25.3 0-46.3-19.7-47.9-45L32.8 140.7c-.4-6.9 5.1-12.7 12-12.7h358.5c6.8 0 12.3 5.8 11.9 12.7z"
                      ></path>
                    </svg>
                  )}
                  {quantity > 1 && (
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      data-icon="minus"
                      style={{ height: 12 }}
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path
                        fill="currentColor"
                        d="M424 318.2c13.3 0 24-10.7 24-24v-76.4c0-13.3-10.7-24-24-24H24c-13.3 0-24 10.7-24 24v76.4c0 13.3 10.7 24 24 24h400z"
                      ></path>
                    </svg>
                  )}
                </button>
                <div
                  className={classNames(
                    "px-2 border-t border-b font-bold flex-center w-8"
                  )}
                  style={{
                    color: Utils.formatColor(product.title.color),
                    borderColor: Utils.lighterColor(0.8, product.title.color)
                  }}
                >
                  {quantity}
                </div>
                <button
                  style={{
                    border: "1px solid",
                    color: Utils.formatColor(product.title.color),
                    borderColor: Utils.lighterColor(0.8, product.title.color)
                  }}
                  className={classNames(
                    "border flex-center p-1 xs:p-2 bg-transparent",
                    {
                      "rounded-l rounded-r-none": rtl,
                      "rounded-r rounded-l-none": !rtl,
                      "opacity-50 cursor-not-allowed":
                        quantity + 1 > maxQuantity
                    }
                  )}
                  disabled={quantity + 1 > maxQuantity}
                  onClick={() => updateItem(+1)}
                >
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    data-icon="plus"
                    style={{ height: 12 }}
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                  >
                    <path
                      fill="currentColor"
                      d="M448 294.2v-76.4c0-13.3-10.7-24-24-24H286.2V56c0-13.3-10.7-24-24-24h-76.4c-13.3 0-24 10.7-24 24v137.8H24c-13.3 0-24 10.7-24 24v76.4c0 13.3 10.7 24 24 24h137.8V456c0 13.3 10.7 24 24 24h76.4c13.3 0 24-10.7 24-24V318.2H424c13.3 0 24-10.7 24-24z"
                    ></path>
                  </svg>
                </button>
              </div>
            )}
            {soldOut && (
              <div className={classNames("flex text-red-500 items-center")}>
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-icon="ban"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  className={classNames("w-4 h-4", rtl ? "ml-1" : "mr-1")}
                >
                  <path
                    fill="currentColor"
                    d="M256 8C119.034 8 8 119.033 8 256s111.034 248 248 248 248-111.034 248-248S392.967 8 256 8zm130.108 117.892c65.448 65.448 70 165.481 20.677 235.637L150.47 105.216c70.204-49.356 170.226-44.735 235.638 20.676zM125.892 386.108c-65.448-65.448-70-165.481-20.677-235.637L361.53 406.784c-70.203 49.356-170.226 44.736-235.638-20.676z"
                  ></path>
                </svg>
                <span>Sold out</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {!!bundles && !soldOut && !discountCode && (
        <Bundles
          id={id}
          line={line}
          bundles={bundles}
          soldOut={{
            get: soldOut,
            set: (v) => setSoldOut(v)
          }}
          maxQuantity={{
            get: maxQuantity,
            set: (v) => setMaxQuantity(v)
          }}
        />
      )}
    </div>
  );
};

export default CartItemsList;
